import { useEffect, useState } from "react";
import { api, fmtEur, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Copy } from "lucide-react";
import { toast } from "sonner";

export const WebdeskBox = ({ p }) => {
  if (!p.numero_fattura && !p.codice_avviso) return null;
  const testo = [p.numero_fattura && `Fattura ${p.numero_fattura}`, p.codice_avviso && `Codice ${p.codice_avviso}`].filter(Boolean).join(" · ");
  const copia = () => { navigator.clipboard?.writeText(p.codice_avviso || p.numero_fattura); toast.success("Copiato"); };
  return (
    <div className="rounded-sm border border-primary/40 bg-primary/5 p-2.5 text-sm" data-testid="webdesk-box">
      <div className="text-xs font-heading uppercase tracking-wider text-primary mb-1">Da segnare pagata su Webdesk</div>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono" data-testid="webdesk-codice">{testo}</span>
        <Button size="sm" variant="outline" className="h-7 gap-1" onClick={copia} data-testid="webdesk-copy"><Copy className="w-3.5 h-3.5" />Copia</Button>
      </div>
    </div>
  );
};

export const PagaDialog = ({ p, onClose, onDone, banche }) => {
  const [f, setF] = useState({});
  const [done, setDone] = useState(null);
  useEffect(() => {
    if (p) { setDone(null); setF({ data_pagamento: new Date().toISOString().slice(0, 10), banca: p.banca || null, prima_nota: true }); }
  }, [p]);
  const paga = async () => {
    try {
      const { data } = await api.post(`/pagamenti/${p.id}/paga`, f);
      toast.success(f.prima_nota ? "Pagato e registrato in Prima Nota" : "Segnato come pagato");
      if (data.numero_fattura || data.codice_avviso) setDone(data); else onDone();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const segnaWebdesk = async () => {
    try { await api.patch(`/pagamenti/${done.id}`, { webdesk_segnato: true }); toast.success("Segnato su Webdesk"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    onDone();
  };
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && (done ? onDone() : onClose())}>
      <DialogContent>
        <DialogHeader><DialogTitle>{done ? "Pagamento registrato" : "Segna come pagato"}</DialogTitle></DialogHeader>
        {p && !done && <>
          <div className="text-sm">{p.beneficiario || p.descrizione} · <span className="font-mono font-bold">{fmtEur(p.importo)}</span></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-heading uppercase">Data pagamento</Label>
              <Input type="date" className="h-8 mt-1" value={f.data_pagamento || ""} onChange={(e) => setF({ ...f, data_pagamento: e.target.value })} data-testid="paga-data" /></div>
            <div><Label className="text-xs font-heading uppercase">Banca</Label>
              <Select value={f.banca || "__none"} onValueChange={(v) => setF({ ...f, banca: v === "__none" ? null : v })}>
                <SelectTrigger className="h-8 mt-1" data-testid="paga-banca"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__none">—</SelectItem>{banche.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox checked={f.prima_nota} onCheckedChange={(v) => setF({ ...f, prima_nota: !!v })} data-testid="paga-prima-nota" />
            Registra anche in Prima Nota
          </label>
          <WebdeskBox p={p} />
        </>}
        {done && <><WebdeskBox p={done} /><div className="text-sm text-muted-foreground">Quando l'hai segnata su Webdesk, conferma qui sotto.</div></>}
        <DialogFooter>
          {done ? <>
            <Button variant="outline" onClick={onDone} data-testid="webdesk-dopo">Lo faccio dopo</Button>
            <Button onClick={segnaWebdesk} data-testid="webdesk-segnato">Fatto, segnata su Webdesk</Button>
          </> : <>
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={paga} data-testid="paga-confirm">Conferma pagamento</Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
